import { useRef, useEffect } from "react";
import { makeStyles, tokens, Text, Spinner, mergeClasses } from "@fluentui/react-components";
import { ErrorCircle16Regular, Search16Regular } from "@fluentui/react-icons";
import SFIcon from "./SFIcon";

/** Maps record type names to SLDS standard icon descriptors */
const RECORD_TYPE_ICON = {
  contact: { category: "standard", name: "contact", title: "Contact" },
  lead: { category: "standard", name: "lead", title: "Lead" },
  account: { category: "standard", name: "account", title: "Account" },
  user: { category: "standard", name: "user", title: "User" },
};

const PHONE_LABEL_COLORS = {
  // Apex response labels
  "Mobile Phone": tokens.colorPaletteRoyalBlueForeground2,
  "Business Phone": tokens.colorPaletteTealForeground2,
  "Account Phone": tokens.colorPaletteTealForeground2,
  "Home Phone": tokens.colorPaletteGreenForeground2,
  // Legacy / fallback labels
  Mobile: tokens.colorPaletteRoyalBlueForeground2,
  Office: tokens.colorPaletteTealForeground2,
  Home: tokens.colorPaletteGreenForeground2,
  Other: tokens.colorNeutralForeground3,
};

const useStyles = makeStyles({
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    maxHeight: "260px",
    overflowY: "auto",
    overflowX: "hidden",
    marginTop: "2px",
  },
  stateRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    color: tokens.colorNeutralForeground3,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "7px 12px",
    cursor: "pointer",
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    transition: "background-color 0.1s",
    minHeight: "48px",
    ":last-child": {
      borderBottom: "none",
    },
  },
  rowActive: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  rowHovered: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  iconCell: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  infoCell: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  nameLine: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  companyLine: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  phoneCell: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
  },
  phoneNumber: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    fontFamily: "monospace",
    letterSpacing: "0.02em",
  },
  phoneLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
    borderRadius: tokens.borderRadiusSmall,
    padding: "0px 5px",
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  retryHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    paddingLeft: "22px",
  },
  msAvatar: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
});

/**
 * PhoneSearchDropdown
 *
 * Renders an accessible, keyboard-navigable result list below the phone input.
 *
 * Props:
 *   id           – DOM id used by the parent input for aria-controls /
 *                  aria-activedescendant wiring
 *   rows         – flat array of { recordId, recordType, name, company, phone, label }
 *   activeIndex  – currently highlighted row index (-1 = none)
 *   isLoading    – show loading spinner
 *   error        – error message string or null
 *   onSelect     – callback(row) invoked on click or Enter
 *   onHoverIndex – callback(index) so parent can sync activeIndex on mouse enter
 *   onClose      – callback() to close the dropdown
 */
export default function PhoneSearchDropdown({
  id,
  rows,
  activeIndex,
  isLoading,
  error,
  onSelect,
  onHoverIndex,
  onClose,
}) {
  const styles = useStyles();
  const listRef = useRef(null);
  const activeRowRef = useRef(null);

  // Scroll the active row into view when keyboard navigating
  useEffect(() => {
    if (activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const isEmpty = !isLoading && !error && rows.length === 0;

  return (
    <div
      ref={listRef}
      id={id}
      role="listbox"
      aria-label="Phone number search results"
      className={styles.dropdown}
    >
      {isLoading && (
        <div className={styles.stateRow}>
          <Spinner size="extra-tiny" />
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Searching…
          </Text>
        </div>
      )}

      {!isLoading && error && (
        <div
          className={styles.stateRow}
          style={{ flexDirection: "column", alignItems: "flex-start", gap: "3px" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <ErrorCircle16Regular
              style={{ color: tokens.colorPaletteRedForeground1, flexShrink: 0 }}
            />
            <Text size={200} className={styles.errorText}>
              {error}
            </Text>
          </div>
          <Text size={100} className={styles.retryHint}>
            Please try again
          </Text>
        </div>
      )}

      {!isLoading && isEmpty && (
        <div className={styles.stateRow}>
          <Search16Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            No search results found
          </Text>
        </div>
      )}

      {!isLoading &&
        !error &&
        rows.map((row, idx) => {
          const isActive = idx === activeIndex;
          const iconInfo = RECORD_TYPE_ICON[row.recordType?.toLowerCase()];
          const labelColor = PHONE_LABEL_COLORS[row.label] || tokens.colorNeutralForeground3;

          return (
            <div
              key={`${row.recordId}-${row.phone}-${idx}`}
              id={id ? `${id}-option-${idx}` : undefined}
              ref={isActive ? activeRowRef : null}
              role="option"
              aria-selected={isActive}
              className={mergeClasses(styles.row, isActive && styles.rowActive)}
              onClick={() => onSelect(row)}
              onMouseEnter={() => onHoverIndex(idx)}
            >
              {/* Record type icon / MS avatar */}
              <span className={styles.iconCell} title={row.recordType}>
                {iconInfo ? (
                  <SFIcon iconInfo={iconInfo} size="x-small" />
                ) : row.source === "microsoft" ? (
                  <span className={styles.msAvatar} aria-hidden="true">
                    {(row.name || "?").charAt(0).toUpperCase()}
                  </span>
                ) : null}
              </span>

              {/* Name + company */}
              <span className={styles.infoCell}>
                <Text block className={styles.nameLine}>
                  {row.name}
                </Text>
                {row.company && (
                  <Text block className={styles.companyLine}>
                    {row.company}
                  </Text>
                )}
              </span>

              {/* Phone number + label — Salesforce rows only (MS uses Teams ID) */}
              {row.source !== "microsoft" && (
                <span className={styles.phoneCell}>
                  <Text className={styles.phoneNumber}>{row.phone}</Text>
                  {row.label && (
                    <Text className={styles.phoneLabel} style={{ color: labelColor }}>
                      {row.label}
                    </Text>
                  )}
                </span>
              )}
            </div>
          );
        })}
    </div>
  );
}
